import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Phone, Mail, ChevronDown } from "lucide-react";
import Seo from "../components/Seo";
import { Reveal, Eyebrow, MaskedLines } from "../components/Reveal";
import { SITE, PRODUCTS } from "../data/products";

const API = `${SITE.backend}/api`;

const inputCls =
  "w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-night placeholder:text-slate-400 transition-colors focus:border-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-600/20";

const Contact = () => {
  const [params] = useSearchParams();
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    product_interest: "General enquiry",
    message: "",
  });

  useEffect(() => {
    const product = params.get("product");
    if (product) setForm((f) => ({ ...f, product_interest: product }));
  }, [params]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await fetch(`${API}/enquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Submission failed");
      }
      toast.success("Enquiry received — our team will get back to you shortly.", {
        duration: 5000,
      });
      setForm({ name: "", company: "", email: "", phone: "", product_interest: "General enquiry", message: "" });
    } catch (err) {
      toast.error(`Could not send your enquiry. Please try again or write to ${SITE.email}.`);
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="page-contact">
      <Seo
        title="Contact"
        description="Request a sample, a quote or technical support from AURIK INGREVIA (P) Ltd. Call +91 9910050608 or write to info@fnni.in."
      />

      <section className="grain mesh-glow relative overflow-hidden pb-16 pt-40 lg:pt-48">
        <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8">
          <Eyebrow dark>Contact</Eyebrow>
          <MaskedLines
            as="h1"
            delay={0.2}
            className="mt-7 max-w-2xl font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl"
            lines={["Let's talk", "about intake."]}
          />
          <p className="mt-7 max-w-xl text-sm leading-relaxed text-emerald-100/70 sm:text-base">
            Samples, quotes, dosage guidance or distribution — send the form and it lands
            directly with our team.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 pb-28 pt-14 sm:px-8">
        <div className="grid gap-12 lg:grid-cols-[1.15fr_0.85fr]">
          <Reveal>
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="rounded-2xl border border-slate-200 bg-white p-7 sm:p-9"
            >
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="cf-name" className="mb-2 block font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                    Name *
                  </label>
                  <input id="cf-name" required minLength={2} data-testid="contact-name-input" value={form.name} onChange={set("name")} className={inputCls} placeholder="Your full name" />
                </div>
                <div>
                  <label htmlFor="cf-company" className="mb-2 block font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                    Company *
                  </label>
                  <input id="cf-company" required minLength={2} data-testid="contact-company-input" value={form.company} onChange={set("company")} className={inputCls} placeholder="Company / feed mill" />
                </div>
                <div>
                  <label htmlFor="cf-email" className="mb-2 block font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                    Email *
                  </label>
                  <input id="cf-email" type="email" required data-testid="contact-email-input" value={form.email} onChange={set("email")} className={inputCls} placeholder="you@company.com" />
                </div>
                <div>
                  <label htmlFor="cf-phone" className="mb-2 block font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                    Phone *
                  </label>
                  <input id="cf-phone" type="tel" required minLength={7} data-testid="contact-phone-input" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="+91 ..." />
                </div>
              </div>

              <div className="mt-5">
                <label htmlFor="cf-product" className="mb-2 block font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Product interest
                </label>
                <div className="relative">
                  <select id="cf-product" data-testid="contact-product-select" value={form.product_interest} onChange={set("product_interest")} className={`${inputCls} appearance-none pr-10`}>
                    <option>General enquiry</option>
                    {PRODUCTS.map((p) => (
                      <option key={p.slug} value={p.name}>{p.name}</option>
                    ))}
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                </div>
              </div>

              <div className="mt-5">
                <label htmlFor="cf-message" className="mb-2 block font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Message *
                </label>
                <textarea
                  id="cf-message"
                  required
                  minLength={10}
                  rows={5}
                  data-testid="contact-message-textarea"
                  value={form.message}
                  onChange={set("message")}
                  className={inputCls}
                  placeholder="Tell us about your feed type, species and what you need — sample, quote or technical guidance."
                />
              </div>

              <button
                type="submit"
                disabled={sending}
                data-testid="contact-submit-btn"
                className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-emerald-900 px-7 py-4 font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-white transition-colors hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              >
                {sending ? "Sending…" : "Send enquiry"}
              </button>
              <p className="mt-4 text-xs text-slate-500">
                Your enquiry is emailed straight to {SITE.email} — no newsletter, no spam.
              </p>
            </form>
          </Reveal>

          <div className="space-y-5">
            <Reveal delay={0.08}>
              <div className="grain relative overflow-hidden rounded-2xl bg-night p-8 text-white" data-testid="contact-info-card">
                <h2 className="font-display text-xl font-extrabold tracking-tight">Talk to us directly</h2>
                <ul className="mt-6 space-y-5">
                  <li>
                    <a href={`tel:${SITE.phone.replace(/\s/g, "")}`} data-testid="contact-info-phone" className="group flex items-center gap-4">
                      <span className="rounded-full border border-emerald-400/30 bg-emerald-400/10 p-3 text-teal-300">
                        <Phone className="h-4 w-4" />
                      </span>
                      <span>
                        <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-300/80">Phone</span>
                        <span className="mt-0.5 block text-sm font-semibold group-hover:text-teal-300">{SITE.phone}</span>
                      </span>
                    </a>
                  </li>
                  <li>
                    <a href={`mailto:${SITE.email}`} data-testid="contact-info-email" className="group flex items-center gap-4">
                      <span className="rounded-full border border-emerald-400/30 bg-emerald-400/10 p-3 text-teal-300">
                        <Mail className="h-4 w-4" />
                      </span>
                      <span>
                        <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-300/80">Email</span>
                        <span className="mt-0.5 block text-sm font-semibold group-hover:text-teal-300">{SITE.email}</span>
                      </span>
                    </a>
                  </li>
                </ul>
              </div>
            </Reveal>

            <Reveal delay={0.14}>
              <div className="rounded-2xl border border-slate-200 bg-white p-8" data-testid="contact-next-steps">
                <h3 className="font-mono text-[10px] font-semibold uppercase tracking-[0.24em] text-teal-700">What happens next</h3>
                <ol className="mt-5 space-y-4">
                  {[
                    "Our team reviews your enquiry and product interest.",
                    "We come back to you by email or phone.",
                    "Samples, quotes and dosage guidance are arranged.",
                  ].map((s, i) => (
                    <li key={s} className="flex gap-4 text-sm text-slate-700">
                      <span className="font-mono text-xs font-semibold text-teal-700">0{i + 1}</span>
                      {s}
                    </li>
                  ))}
                </ol>
              </div>
            </Reveal>

            <Reveal delay={0.2}>
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50/60 p-8" data-testid="contact-group-note">
                <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.24em] text-teal-700">Group affiliation</p>
                <p className="mt-3 text-sm leading-relaxed text-slate-700">
                  AURIK INGREVIA (P) Ltd. is a division / affiliate of{" "}
                  <span className="font-semibold text-emerald-900">{SITE.groupCompany}</span>.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
